import { useState, useEffect, useContext } from 'react'
import { useNavigate } from 'react-router-dom'
import Skeleton from 'react-loading-skeleton'
import { RadioGroup } from '@headlessui/react'
import { MeContext } from 'contexts/me'

// Components
import PageWrapper from 'components/PageWrapper'
import { AdvanceOptionCard } from 'components/AdvanceOffer'
import Badge from 'components/Badge'
import PrimaryButton from 'components/primaryButton'
import WORDING from 'constants/wording'

// Api
import { getCreditLinesSummary } from 'api/advances'

// Utils | constants
import { hasSuspendedMembership } from 'utils'
import { toLocaleCurrency } from 'utils/dinero'
import { INTEREST_FREE_ADVANCE_MAX_AMOUNT } from 'constants/credit'
import { trackEvent } from 'analytics/trackFunctions'
import SuspendedMembership from 'components/SuspendedMembership'
import { LineSummary } from 'types/api/loan'
import { DepletedInterestFreeModal } from './components/DepletedInterestFreeAdvances'

const OPTIONS = ['zero-rate', 'normal-rate']

const AdvanceTypeOptions = () => {
  const [option, setOption] = useState(null)
  const [creditLine, setCreditLine] = useState<LineSummary>(null)
  const [isDepletedModalOpen, setIsDepletedModalOpen] = useState<boolean>(false)
  const navigate = useNavigate()

  const { me } = useContext(MeContext)

  useEffect(() => {
    trackEvent({
      eventName: 'bille_pantalla_adelantos_seleccion_tipo_adelanto'
    })

    getCreditLinesSummary().then((res) => setCreditLine(res))
  }, [])

  return (
    <>
      <PageWrapper noTabBar viewSelected='/adelantos' title='Pedir adelanto'>
        <section className='sm:max-w-sm !h-[78vh] md:h-auto flex flex-col text-center items-center sm:mt-0 mx-auto'>
          <h1 className='font-bold text-xl mb-1'>¿Cuánto querés pedir?</h1>
          <p className='text-xs'>Todos tienen acreditación inmediata.</p>
          {creditLine ? (
            <RadioGroup
              value={option}
              onChange={setOption}
              className='space-y-2 mt-4 w-full'
            >
              <AdvanceOptionCard
                value={OPTIONS[0]}
                title={`Hasta ${
                  creditLine.isValid && !creditLine.interestFreeIsDepleted
                    ? `$${toLocaleCurrency(
                        creditLine.interestFreeRemainingAmount
                      )}`
                    : `$${toLocaleCurrency(INTEREST_FREE_ADVANCE_MAX_AMOUNT)}`
                }`}
                description='Adelanto a Tasa 0%.'
                disabled={hasSuspendedMembership(me?.subscriber_products)}
                badge={
                  <Badge variant='GRADIENT_GREEN'>
                    <span>Sin interés</span>
                  </Badge>
                }
              />
              <AdvanceOptionCard
                value={OPTIONS[1]}
                disabled={hasSuspendedMembership(me?.subscriber_products)}
                title={`Más de ${
                  creditLine.isValid &&
                  creditLine.interestFreeRemainingAmount > 0
                    ? `$${toLocaleCurrency(
                        creditLine.interestFreeRemainingAmount
                      )}`
                    : `$${toLocaleCurrency(INTEREST_FREE_ADVANCE_MAX_AMOUNT)}`
                }`}
                badge={
                  <Badge variant='SILVER'>
                    <span>Con interés</span>
                  </Badge>
                }
              />
            </RadioGroup>
          ) : (
            <div className='w-full'>
              <Skeleton count={2} height={64} className='my-2 w-full' />
            </div>
          )}

          {hasSuspendedMembership(me?.subscriber_products) && (
            <SuspendedMembership
              className='border border-red-900 mt-7'
              paragraphText={WORDING.SUSPENDED_MEMBERSHIP_ADVANCE}
            />
          )}

          <PrimaryButton
            buttonText='Continuar'
            className='mt-auto sm:mt-14'
            primaryButtonFunction={() => {
              if (option === OPTIONS[0]) {
                if (
                  creditLine.isValid &&
                  creditLine.interestFreeRemainingAmount === 0
                ) {
                  setIsDepletedModalOpen(true)
                  return
                }

                trackEvent({
                  eventName:
                    'bille_adelantos_seleccion_tipo_adelanto_opcion_tasa_0 '
                })
                navigate('/pedir-adelanto/sin-interes')
              } else {
                trackEvent({
                  eventName:
                    'bille_pantalla_adelantos_seleccion_tipo_opcion_con_interes'
                })
                navigate('/pedir-adelanto')
              }
            }}
            isDisabled={!option}
          />
        </section>
      </PageWrapper>
      <DepletedInterestFreeModal
        isOpen={isDepletedModalOpen}
        setIsOpen={setIsDepletedModalOpen}
        expirationDate={creditLine?.expiresAt}
      />
    </>
  )
}

export default AdvanceTypeOptions
